import React, { useEffect, useState } from 'react';
import { PublicHeader } from '../components/PublicHeader';
import { motion } from 'framer-motion';
import { Play, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthDialog from '../components/AuthDialog';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VideosPreviewPage() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/videos`);
      setVideos(response.data);
    } catch (error) {
      console.error('Erro ao carregar vídeos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoClick = (video) => {
    if (video.plan_required === 'PREMIUM' && !user) {
      setShowAuthDialog(true);
      return;
    }
    
    if (user) {
      navigate('/videos');
    } else {
      setShowAuthDialog(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <PublicHeader />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      <div className="pt-24 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Vídeos Educacionais
            </h1>
            <p className="text-xl text-muted-foreground">
              Aprenda com conteúdo de qualidade do Beto Carvalho
            </p>
          </motion.div>

          {videos.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground">Vídeos serão adicionados em breve!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videos.map((video, index) => (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => handleVideoClick(video)}
                  data-testid={`video-preview-${index}`}
                  className="video-card rounded-2xl overflow-hidden bg-white border border-border cursor-pointer"
                >
                  <div className="relative aspect-video bg-muted">
                    <img
                      src={video.thumbnail || `https://img.youtube.com/vi/${video.youtube_id}/maxresdefault.jpg`}
                      alt={video.titulo}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center group-hover:bg-black/40 transition-all">
                      {video.plan_required === 'PREMIUM' && !user ? (
                        <div className="h-16 w-16 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center">
                          <Lock className="h-8 w-8 text-primary" />
                        </div>
                      ) : (
                        <div className="h-16 w-16 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:scale-110 transition-transform">
                          <Play className="h-8 w-8 text-primary ml-1" />
                        </div>
                      )}
                    </div>
                    {video.plan_required === 'PREMIUM' && (
                      <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-gradient-to-r from-primary to-secondary text-white text-xs font-semibold">
                        PREMIUM
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-lg mb-2 line-clamp-2">{video.titulo}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">{video.descricao}</p>
                    {video.categoria && (
                      <div className="mt-3">
                        <span className="inline-block px-3 py-1 rounded-full bg-muted text-xs font-medium">
                          {video.categoria}
                        </span>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      <AuthDialog open={showAuthDialog} onClose={() => setShowAuthDialog(false)} />
    </div>
  );
}
